import { faFile, faFilePdf, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Modal } from "react-bootstrap";
import moment from "moment";

export interface AttachedDoc {
  url: string;
  name?: string;
  size?: number;
  uploadedAt?: string;
}

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

function extensionOf(value?: string) {
  return String(value || "").split("?")[0].split(".").pop()?.toLowerCase() || "";
}

export function docKind(doc: AttachedDoc): "image" | "pdf" | "unknown" {
  for (const candidate of [doc.name, doc.url]) {
    const ext = extensionOf(candidate);
    if (IMAGE_EXTENSIONS.indexOf(ext) !== -1) return "image";
    if (ext === "pdf") return "pdf";
  }
  return "unknown";
}

export function formatSize(bytes?: number) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function candidateDocs(user: any): AttachedDoc[] {
  const docs = user?.qualificationDocs;
  if (Array.isArray(docs) && docs.length) {
    return docs
      .filter((d: any) => d && d.url)
      .map((d: any) => ({
        url: d.url,
        name: d.name || String(d.url).split("/").pop(),
        size: d.size,
        uploadedAt: d.uploadedAt,
      }));
  }
  if (user?.qualificationDoc) {
    return [{ url: user.qualificationDoc, name: String(user.qualificationDoc).split("/").pop() }];
  }
  return [];
}

export function DocumentList({
  docs,
  onView,
  emptyText = "No document uploaded",
}: {
  docs: AttachedDoc[];
  onView: (doc: AttachedDoc) => void;
  emptyText?: string;
}) {
  if (!docs.length) {
    return <div className="text-muted py-3">{emptyText}</div>;
  }

  return (
    <div className="d-flex flex-column gap-2">
      {docs.map((doc, i) => {
        const kind = docKind(doc);
        return (
          <div
            key={`${doc.url}-${i}`}
            className="d-flex align-items-center gap-3 p-2 border rounded"
          >
            {kind === "image" ? (
              <img
                src={doc.url}
                alt=""
                style={{
                  width: 44,
                  height: 44,
                  objectFit: "cover",
                  borderRadius: 4,
                  border: "1px solid #dee2e6",
                  flexShrink: 0,
                }}
              />
            ) : (
              <span
                className="d-flex align-items-center justify-content-center text-secondary"
                style={{ width: 44, height: 44, flexShrink: 0, fontSize: 20 }}
              >
                <FontAwesomeIcon icon={kind === "pdf" ? faFilePdf : faFile} />
              </span>
            )}
            <div className="flex-grow-1 min-w-0">
              <div className="text-truncate fs-12 fw-semibold">{doc.name || "Document"}</div>
              <div className="text-muted fs-12">
                {[
                  formatSize(doc.size),
                  doc.uploadedAt ? moment(doc.uploadedAt).format("DD MMM YYYY") : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <Button variant="outline-primary" size="sm" onClick={() => onView(doc)}>
              View
            </Button>
          </div>
        );
      })}
    </div>
  );
}

export function DocumentPreviewModal({
  doc,
  title = "Document",
  subtitle,
  onHide,
}: {
  doc: AttachedDoc | null;
  title?: string;
  subtitle?: string;
  onHide: () => void;
}) {
  const kind = doc ? docKind(doc) : "unknown";

  return (
    <Modal show={!!doc} onHide={onHide} fullscreen scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-bold">
          {title}
          {subtitle ? <span className="text-muted fw-normal"> — {subtitle}</span> : null}
          {doc?.name ? <div className="text-muted fw-normal fs-12">{doc.name}</div> : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light d-flex flex-column p-0">
        {!doc ? null : kind === "image" ? (
          <div className="d-flex align-items-center justify-content-center flex-grow-1 p-3">
            <img
              src={doc.url}
              alt={doc.name || "Document"}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          </div>
        ) : kind === "pdf" ? (
          <iframe
            src={doc.url}
            title={doc.name || "Document"}
            style={{ border: "none", width: "100%", flexGrow: 1, minHeight: "70vh" }}
          />
        ) : (
          <div className="text-center py-5">
            <p className="text-muted mb-3">
              This file is not an image or a PDF, so it cannot be previewed here.
            </p>
            <a href={doc.url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">
              Open in new tab
            </a>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {doc && (
          <a
            href={doc.url}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-secondary btn-sm"
          >
            <FontAwesomeIcon icon={faUpRightFromSquare} className="me-2" />
            Open in new tab
          </a>
        )}
        <Button variant="light" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
